import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { IMAGES } from "@/data/site";

const reveal = {
  hidden: { opacity: 0, y: 28 },
  show: (i) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, delay: i * 0.12, ease: [0.22, 1, 0.36, 1] },
  }),
};

export const MeetNicole = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-6%", "10%"]);

  return (
    <section
      ref={ref}
      id="meet-nicole"
      data-testid="meet-nicole-section"
      className="relative py-24 sm:py-32 bg-sand"
    >
      <div className="mx-auto max-w-7xl px-5 sm:px-8 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
        {/* Sticky portrait */}
        <div className="lg:col-span-5">
          <div className="lg:sticky lg:top-28">
            <motion.div
              initial={{ opacity: 0, scale: 1.04 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 1 }}
              className="relative overflow-hidden rounded-[2rem] soft-shadow-lg aspect-[4/5]"
            >
              <motion.img
                src={IMAGES.nicolePortrait}
                alt="Nicole, Certified Nursing Assistant"
                style={{ y }}
                className="absolute inset-0 h-[112%] w-full object-cover"
              />
            </motion.div>
            <p className="mt-4 text-center eyebrow">Nicole · CNA · Grand Junction, CO</p>
          </div>
        </div>

        {/* Biography */}
        <div className="lg:col-span-7 lg:pt-6">
          <motion.p custom={0} variants={reveal} initial="hidden" whileInView="show" viewport={{ once: true }} className="eyebrow mb-5">
            Meet Nicole
          </motion.p>
          <motion.h2 custom={1} variants={reveal} initial="hidden" whileInView="show" viewport={{ once: true }} className="font-serif text-4xl sm:text-5xl text-charcoal leading-tight tracking-tight">
            Care given the way she'd want her own family cared for.
          </motion.h2>

          <div className="mt-8 space-y-6 text-lg text-charcoal-soft leading-relaxed max-w-2xl">
            <motion.p custom={2} variants={reveal} initial="hidden" whileInView="show" viewport={{ once: true }}>
              <span className="float-left font-serif text-6xl leading-[0.8] text-sage-700 mr-3 mt-1">F</span>
              or more than fifteen years, Nicole has dedicated her career to caring
              for others — with the same attentiveness, patience, and respect she
              would want for her own family.
            </motion.p>
            <motion.p custom={3} variants={reveal} initial="hidden" whileInView="show" viewport={{ once: true }}>
              As a Certified Nursing Assistant, she has built her reputation on a
              single, unwavering standard: the people in her care always come first.
            </motion.p>
            <motion.p custom={4} variants={reveal} initial="hidden" whileInView="show" viewport={{ once: true }} className="text-charcoal font-medium">
              Today, she brings that same dedication directly into your home —
              offering personal, one-on-one care built on trust, continuity, and
              a genuine relationship with every family she serves.
            </motion.p>
          </div>

          <motion.div
            custom={5}
            variants={reveal}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
            className="mt-12 grid grid-cols-2 sm:grid-cols-4 gap-8 border-t border-charcoal/10 pt-10"
          >
            {[
              ["15+", "Years of experience"],
              ["CNA", "Certified & credentialed"],
              ["1", "Consistent caregiver"],
              ["100%", "Heart in every visit"],
            ].map(([big, small]) => (
              <div key={small}>
                <p className="font-serif text-3xl text-sage-700 leading-none">{big}</p>
                <p className="text-sm text-charcoal-soft mt-2">{small}</p>
              </div>
            ))}
          </motion.div>
        </div>
      </div>
    </section>
  );
};
